import { elementTypes } from '@scene'
import { roofStoreyKey } from './constants'

/** Seed available storeys from storey boundaries plus the canonical roof key. */
export const availableStoreysFromBoundaries = (storeyBoundaries: StoreyBoundaries): Set<string> =>
  new Set([...Object.keys(storeyBoundaries), roofStoreyKey])

const withStoreyAsString = <T extends { storey?: string | number }>(element: T): T => ({
  ...element,
  ...(element.storey != null ? { storey: String(element.storey) } : {}),
})

const withElementType = <T extends { element_type?: ElementTypes }>(
  element: T,
  elementType: ElementTypes,
): T => ({
  ...element,
  element_type: element.element_type ?? elementType,
})

const withRoofStorey = <T extends { storey?: string | number }>(element: T): T =>
  withStoreyAsString({
    ...element,
    storey: element.storey ?? roofStoreyKey,
  })

const prepareVerticalSlab = <T extends VerticalSlab>(slab: T, elementType: ElementTypes): T => {
  const withStorey = withStoreyAsString(slab)
  const settings = withStorey.positionPlanSettings
  return {
    ...withElementType(withStorey, elementType),
    positionPlanSettings: {
      ...settings,
      storeysForPlans: settings.storeysForPlans.map(storey => String(storey)),
    },
    certificateSettings: withStorey.certificateSettings,
    ...(elementType === 'vertical_slabs'
      ? {
          metadata: withStorey.metadata ?? { use_global_live_loads: true },
        }
      : {}),
    beam: withStorey.beam
      ? withElementType(
          withStoreyAsString(withStorey.beam),
          elementType === 'vertical_roof_slabs' ? 'roof_slab_beams' : 'slab_beams',
        )
      : withStorey.beam,
  }
}

/** Beams, purlins, columns, rips and lintels: plan storeys as strings, like the storeys. */
const prepareLineElement = <
  T extends { positionPlanSettings: LineLikePositionPlanSettings; element_type?: ElementTypes },
>(
  element: T,
  elementType: ElementTypes,
): T => {
  const settings = element.positionPlanSettings
  return {
    ...withElementType(element, elementType),
    positionPlanSettings: {
      ...settings,
      storeysForPlans: settings.storeysForPlans.map(storey => String(storey)),
    },
  }
}

const isRip = (position: Position): position is Rip => position.element_type === 'rips'

const isLintel = (position: Position): position is Lintel => position.element_type === 'lintels'

const splitWallPositions = (
  model: PlanarModel | PlanarModelApiResponse,
): { rips: Rip[]; lintels: Lintel[] } => {
  if ('wall_positions' in model && Array.isArray(model.wall_positions)) {
    return {
      rips: model.wall_positions.filter(isRip),
      lintels: model.wall_positions.filter(isLintel),
    }
  }

  const planarModel = model as PlanarModel
  return {
    rips: planarModel.rips ?? [],
    lintels: planarModel.lintels ?? [],
  }
}

/** Map API walls to FE visibility types and normalize storey keys for filtering. */
export const prepareModelForStore = (model: PlanarModel | PlanarModelApiResponse): PlanarModel => {
  const { rips, lintels } = splitWallPositions(model)

  return {
    walls: model.walls.map(el =>
      withStoreyAsString({
        ...el,
        element_type: el.placement === 'External' ? 'outer_walls' : ('inner_walls' as ElementTypes),
      }),
    ),
    slabs: model.slabs.map(el => withElementType(withStoreyAsString(el), 'slabs')),
    roof_slabs: model.roof_slabs.map(el => withElementType(withRoofStorey(el), 'roof_slabs')),
    vertical_slabs: model.vertical_slabs.map(el => prepareVerticalSlab(el, 'vertical_slabs')),
    vertical_roof_slabs: model.vertical_roof_slabs.map(el =>
      prepareVerticalSlab(withRoofStorey(el), 'vertical_roof_slabs'),
    ),
    beams: model.beams.map(el => prepareLineElement(withStoreyAsString(el), 'beams')),
    columns: model.columns.map(el => prepareLineElement(withStoreyAsString(el), 'columns')),
    purlins: model.purlins.map(el => prepareLineElement(withRoofStorey(el), 'purlins')),
    rips: rips.map(el => prepareLineElement(withStoreyAsString(el), 'rips')),
    lintels: lintels.map(el => prepareLineElement(withStoreyAsString(el), 'lintels')),
    foundationElements: model.foundationElements.map(el =>
      withElementType(withStoreyAsString(el), 'foundation'),
    ),
    storey_boundaries: model.storey_boundaries,
  }
}

export const flattenDomains = (model: PlanarModel) => {
  const slabBeams = [...model.vertical_slabs.map(el => el.beam)]
  const roofSlabBeams = [...model.vertical_roof_slabs.map(el => el.beam)]
  return [
    ...model.walls,
    ...model.vertical_slabs,
    ...model.beams,
    ...model.vertical_roof_slabs,
    ...model.columns,
    ...model.purlins,
    ...slabBeams,
    ...roofSlabBeams,
  ].reduce((acc: Domain[], element) => {
    return [
      ...acc,
      ...(element.domains || []).map(domain => ({
        ...domain,
        element_guid: element.guid,
        length: domain.end.sub(domain.start).length(),
      })),
    ]
  }, [])
}

export const createInteractableByType = (types: ElementTypes[]) => {
  return elementTypes.reduce(
    (collector, type) => ({ ...collector, [type]: types.includes(type) }),
    {} as Record<ElementTypes, boolean>,
  )
}

/** Show the storey itself and every element type in that storey. */
export const includeStoreyInVisibilitySets = (
  visibleStoreys: Set<string>,
  visibilityByTypeAndStorey: VisibilityByTypeAndStorey,
  storey: string,
): void => {
  visibleStoreys.add(storey)
  Object.values(visibilityByTypeAndStorey).forEach(storeys => {
    // Undefined means all storeys are already visible.
    if (storeys) storeys.add(storey)
  })
}

/** Drop a removed storey from storey-level and per-type visibility. */
export const excludeStoreyFromVisibilitySets = (
  visibleStoreys: Set<string>,
  visibilityByTypeAndStorey: VisibilityByTypeAndStorey,
  storey: string,
): void => {
  visibleStoreys.delete(storey)
  Object.values(visibilityByTypeAndStorey).forEach(storeys => {
    if (storeys) storeys.delete(storey)
  })
}

interface StoreyFilters {
  availableStoreys: Set<string>
  visibleStoreys: Set<string>
  visibilityByTypeAndStorey: VisibilityByTypeAndStorey
  storeyBoundaries: StoreyBoundaries
}

const ELEVATION_TOLERANCE = 1e-6

/**
 * Maps the storey keys of the current model to those of the next one. Storey keys are positions,
 * so a storey inserted or removed below shifts them: storeys are matched by their lower elevation
 * first, then by key (a storey whose height changed keeps its key). Unmatched storeys are gone.
 */
export const matchStoreys = (current: StoreyFilters, next: StoreyFilters): Map<string, string> => {
  const matches = new Map<string, string>()
  const unmatchedNext = new Set(next.availableStoreys)
  const match = (storey: string, nextStorey: string) => {
    matches.set(storey, nextStorey)
    unmatchedNext.delete(nextStorey)
  }
  if (current.availableStoreys.has(roofStoreyKey) && unmatchedNext.has(roofStoreyKey)) {
    match(roofStoreyKey, roofStoreyKey)
  }
  current.availableStoreys.forEach(storey => {
    const lower = current.storeyBoundaries[storey]?.[0]
    if (matches.has(storey) || lower === undefined) return
    const nextStorey = [...unmatchedNext].find(candidate => {
      const candidateLower = next.storeyBoundaries[candidate]?.[0]
      return candidateLower !== undefined && Math.abs(candidateLower - lower) < ELEVATION_TOLERANCE
    })
    if (nextStorey !== undefined) match(storey, nextStorey)
  })
  current.availableStoreys.forEach(storey => {
    if (!matches.has(storey) && unmatchedNext.has(storey)) match(storey, storey)
  })
  return matches
}

const setsEqual = (a: Set<string>, b: Set<string>) =>
  a.size === b.size && [...a].every(value => b.has(value))

/**
 * The user's storey filters carried over a model refetch: storeys keep their visibility (also when
 * their key shifted), storeys that no longer exist are dropped. Storeys that are new since the last
 * model (e.g. added by an IFC import) start visible in a filter that showed every storey, and stay
 * hidden in a narrowed one (2D on a single storey, the plan view, the whole model hidden). Filter
 * sets whose content did not change are returned as they were, so subscribers don't re-render.
 */
export const mergeStoreyFilters = (
  current: StoreyFilters,
  next: StoreyFilters,
): Pick<StoreyFilters, 'visibleStoreys' | 'visibilityByTypeAndStorey'> => {
  const matches = matchStoreys(current, next)
  const matchedNext = new Set(matches.values())
  const keep = (visible: Set<string>, initial: Set<string>, universe: string[]) => {
    const showedAll = universe.every(storey => visible.has(storey))
    const merged = new Set([
      ...[...visible].flatMap(storey => {
        const nextStorey = matches.get(storey)
        return nextStorey === undefined ? [] : [nextStorey]
      }),
      ...(showedAll ? [...initial].filter(storey => !matchedNext.has(storey)) : []),
    ])
    return setsEqual(merged, visible) ? visible : merged
  }
  const available = [...current.availableStoreys]
  const availableNonRoof = available.filter(storey => storey !== roofStoreyKey)
  let typeSetsChanged = false
  const visibilityByTypeAndStorey = Object.fromEntries(
    Object.entries(next.visibilityByTypeAndStorey).map(([type, initial]) => {
      const visible = current.visibilityByTypeAndStorey[type as ElementTypes]
      // Undefined means all storeys are visible: that stays so.
      const merged = visible ? keep(visible, initial, availableNonRoof) : visible
      if (merged !== visible) typeSetsChanged = true
      return [type, merged]
    }),
  ) as VisibilityByTypeAndStorey
  return {
    visibleStoreys: keep(current.visibleStoreys, next.visibleStoreys, available),
    visibilityByTypeAndStorey: typeSetsChanged
      ? visibilityByTypeAndStorey
      : current.visibilityByTypeAndStorey,
  }
}
